// FRONTEND src/components/AchievementsSection.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 22.04.2025 to use AchievementsContext and prevent excessive API calls
//

import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import SectionTitle from '../common/SectionTitle';
import { getUserLanguage } from '../../utils/language';
import { useAchievements } from '../../contexts/AchievementsContext';

const AchievementsSection = () => {
  const { achievements, stats, loading, error } = useAchievements();
  const [localAchievements, setLocalAchievements] = useState([]);
  const [localStats, setLocalStats] = useState([]);
  const language = getUserLanguage();

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { ref: counterRef, inView: countersInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    setLocalAchievements(Array.isArray(achievements) ? achievements : []);
    setLocalStats(Array.isArray(stats) ? stats : []);
  }, [achievements, stats]);

  if (loading) {
    return (
      <section id="achievements" className="py-20 bg-black relative">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? 'Naše úspěchy' : 'Our Achievements'}
            title={language === 'cs' ? 'Cesta k' : 'Road to'}
            highlightedText={language === 'cs' ? 'vítězství' : 'victory'}
            description={language === 'cs' ? 'Načítání...' : 'Loading...'}
          />
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="achievements" className="py-20 bg-black relative">
        <div className="container mx-auto px-4 sm:px-6">
          <SectionTitle
            badge={language === 'cs' ? 'Naše úspěchy' : 'Our Achievements'}
            title={language === 'cs' ? 'Cesta k' : 'Road to'}
            highlightedText={language === 'cs' ? 'vítězství' : 'victory'}
            description={error}
          />
        </div>
      </section>
    );
  }

  const sortedAchievements = localAchievements.sort((a, b) => a.year - b.year);
  const latestAchievements = sortedAchievements.slice(-3);

  return (
    <section id="achievements" className="py-20 bg-black relative" ref={sectionRef}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-red-600/5 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-1/4 h-1/4 bg-red-600/5 blur-3xl rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <SectionTitle
          badge={language === 'cs' ? 'Naše úspěchy' : 'Our Achievements'}
          title={language === 'cs' ? 'Cesta k' : 'Road to'}
          highlightedText={language === 'cs' ? 'vítězství' : 'victory'}
          description={
            language === 'cs'
              ? 'Podívejte se na naše úspěchy a ocenění, kterých náš tým dosáhl od svého založení. Každé vítězství a každá trofej je výsledkem tvrdé práce, odhodlání a vášně pro drift.'
              : 'Check out our achievements and awards that our team has earned since its founding. Every victory and trophy is the result of hard work, determination, and passion for drifting.'
          }
        />

        <div ref={counterRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {localStats.map((stat, index) => (
            <div
              key={stat.id || index}
              className="achievement-counter"
              style={{ animationDelay: `${0.2 * (index + 1)}s`, opacity: countersInView ? 1 : 0 }}
            >
              <div className="trophy-container">
                <i className={`fas fa-${stat.icon || 'trophy'} trophy-icon`}></i>
                <div className="trophy-glow"></div>
              </div>
              <div className="counter-number">{stat.value || '0'}</div>
              <div className="counter-title">{stat.translations?.[language]?.title || stat.title || (language === 'cs' ? 'Bez názvu' : 'No title')}</div>
            </div>
          ))}
        </div>

        <h3 className="text-2xl font-title font-bold text-white text-center mb-12">
          {language === 'cs' ? 'Klíčové milníky' : 'Key Milestones'}
        </h3>

        <div className="timeline-container">
          {latestAchievements.map((achievement, index) => (
            <div
              key={achievement.id || index}
              className={`timeline-item ${inView ? 'animate-fade-in' : 'opacity-0'}`}
              style={{ animationDelay: `${0.2 * index}s` }}
            >
              <div className="timeline-year">{achievement.year || 'Není rok'}</div>
              <div className="timeline-card">
                <h4 className="text-xl font-title font-bold text-white mb-2">
                  {achievement.title || (language === 'cs' ? 'Bez názvu' : 'No title')}
                </h4>
                <p className="text-gray-300">
                  {achievement.description || (language === 'cs' ? 'Bez popisu' : 'No description')}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <a href="/achievements-gallery" className="btn-outline">
            <i className="fas fa-images text-xs mr-2"></i>
            {language === 'cs' ? 'Prohlédnout galerii úspěchů' : 'View Achievements Gallery'}
          </a>
        </div>
      </div>
    </section>
  );
};

export default AchievementsSection;